<script lang="ts" setup>
    import { computed, onMounted, reactive, ref } from 'vue';
    import {PageTitle} from '../../LibMain';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import { PrivilegeType, Alignment, CompanySettingParameterNames } from "../../Enums/ChoiceEnums";
    import { IDefaultProviderSetup, IEstimateAutoGenerateSetup, IDefaultLocationSetup, IDefaultApptStatusSetup,
        IDefaultApptTypeSetup, IDefaultResourceSetup, IAutoGenerateLogResults } from '../../lib/Models/PatientEstimatorModel';
    import { saveEstimation, costEstimator } from '../../lib/ChoicePatientEstimatorApi';
    import { popAlert } from '../../lib/Notification';
    import vSelect from 'vue-select'; 
    import * as ReportModel from '../../lib/Models/Reports/BillerProductivityReportModel';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import IntegerInput from '../../components/IntegerInput.vue';


    const props = withDefaults(defineProps<{
        screenName: string,
        privilegeLevel: PrivilegeType,
        lookupProvider: ReportModel.ILookupList[],
        lookupLocation: ReportModel.ILookupList[],
        lookupAppointmentStatus: ReportModel.ILookupList[],
        lookupAppointmentType: ReportModel.ILookupList[],
        lookupResource: ReportModel.ILookupList[]
    }>(), {
    });

    const logColumns = ref<ICol[]>([
        { Name: 'Log Id', DataField: 'logId' },
        { Name: 'Appt Create Date', DataField: 'appointmentCreateDate', RenderMode: RenderModeType.DateTime },
        { Name: 'Auto Gen Date', DataField: 'logDate', RenderMode: RenderModeType.DateTime },
        { Name: 'Appt Id', DataField: 'appointmentId' },
        { Name: 'Appt Date', DataField: 'appointmentDate', RenderMode: RenderModeType.DateTime },
        { Name: 'Patient Id', DataField: 'patientId' },
        { Name: 'Created Estimate?', DataField: 'isEstimateCreated', RenderMode: RenderModeType.BoolYesNo },
        { Name: 'Estimate Id', DataField: 'estimateId' },
        { Name: 'Log Description', DataField: 'logDescription' }
    ]);

    onMounted(async () => { 
        const lookupAllValue = -999;
        setupData.providerList = props.lookupProvider.filter(item => +item.encode != lookupAllValue);
        setupData.locationList = props.lookupLocation.filter(item => +item.encode != lookupAllValue);
        setupData.appointmentStatusList = props.lookupAppointmentStatus;
        setupData.appointmentTypeList = props.lookupAppointmentType;
        setupData.resourceList = props.lookupResource;

        await Promise.all([LoadSetupSettings(), LoadLogResults()]);
    });

    const tabs = ref([
        { name: 'Provider' },
        { name: 'Service Location' },
        { name: 'Appointment Status' },
        { name: 'Appointment Type' },
        { name: 'Resource' },
        { name: 'Log' }
        ]);

    const activeTab = ref('Provider');

    const setupData = reactive<{
        providerList: ReportModel.ILookupList[],
        selectedProviderEntity: number,
        setupSettings?: IEstimateAutoGenerateSetup,
        defaultProviderData?: IDefaultProviderSetup,
        locationList: ReportModel.ILookupList[],
        selectedLocationEntity: number,
        defaultLocationData?: IDefaultLocationSetup,
        appointmentStatusList: ReportModel.ILookupList[],
        selectedAppointmentStatusId: number[],
        defaultApptStatusData?: IDefaultApptStatusSetup,
        appointmentTypeList: ReportModel.ILookupList[],
        selectedAppointmentTypeId: number[],
        defaultApptTypeData?: IDefaultApptTypeSetup,
        resourceList: ReportModel.ILookupList[],
        selectedResourceId: number[],
        defaultResourceData?: IDefaultResourceSetup
        unassigned: number
    }>({
        providerList: [],
        selectedProviderEntity: 0,
        locationList: [],
        selectedLocationEntity: 0,
        appointmentStatusList: [],
        appointmentTypeList: [],
        resourceList: [],
        selectedAppointmentStatusId: [-999],
        selectedAppointmentTypeId: [-999],
        selectedResourceId: [-999],
        unassigned: 0
    });

    const logData = reactive<{
        autoGenLogResult: IAutoGenerateLogResults[],
        daysBack: number,
        maxDaysBack: number,
        daysAheadParam?: string
    }>({
        autoGenLogResult: [],
        daysBack: 3,
        maxDaysBack: 90
    });

    async function closeWindow() {
        window.close();
    }

    const showProviderSaveButton = computed<boolean>(() => {
        return !setupData.selectedProviderEntity || !hasPrivilege
    });

    const showLocationSaveButton = computed<boolean>(() => {
        return !setupData.selectedLocationEntity || !hasPrivilege
    });

    const showApptStatusSaveButton = computed<boolean>(() => {
        if (!setupData.selectedAppointmentStatusId.length || !hasPrivilege) {
            return false;
        }
        
        //Check if the array contains only the number 0, which is Unassigned
        if (setupData.selectedAppointmentStatusId.length === 1 && setupData.selectedAppointmentStatusId[0] === setupData.unassigned) {
            return false;
        }

        return true; 
    });

    const showApptTypeSaveButton = computed<boolean>(() => {
        if (!setupData.selectedAppointmentTypeId.length || !hasPrivilege) {
            return false;
        }

        if (setupData.selectedAppointmentTypeId.length === 1 && setupData.selectedAppointmentTypeId[0] === setupData.unassigned) {
            return false;
        }

        return true; 
    });

    const showResourceSaveButton = computed<boolean>(() => {
        if (!setupData.selectedResourceId.length || !hasPrivilege) {
            return false;
        }

        if (setupData.selectedResourceId.length === 1 && setupData.selectedResourceId[0] === setupData.unassigned) {
            return false;
        }

        return true; 
    });

    const showSaveAllButton = computed<boolean>(() => {
        if (!setupData.selectedResourceId.length || !hasPrivilege) {
            return false;
        }
        if (setupData.selectedResourceId.length === 1 && setupData.selectedResourceId[0] === setupData.unassigned) {
            return false;
        }
        if (!setupData.selectedAppointmentTypeId.length) {
            return false;
        }
        if (setupData.selectedAppointmentTypeId.length === 1 && setupData.selectedAppointmentTypeId[0] === setupData.unassigned) {
            return false;
        }
        if (!setupData.selectedAppointmentStatusId.length) {
            return false;
        }
        if (setupData.selectedAppointmentStatusId.length === 1 && setupData.selectedAppointmentStatusId[0] === setupData.unassigned) {
            return false;
        }
        if (!setupData.selectedLocationEntity) {
            return false;
        }
        if (setupData.unassigned === setupData.selectedLocationEntity) {
            return false;
        }
        if (!setupData.selectedProviderEntity) {
            return false;
        }
        if (setupData.unassigned === setupData.selectedProviderEntity) {
            return false;
        }

        return true; 
    });
    
    const hasPrivilege = computed<boolean>(() => props.privilegeLevel >= PrivilegeType.ReadUpdate);

    const errorDaysBack = computed<boolean>(() => {        
        return logData.daysBack < 1 || logData.daysBack > logData.maxDaysBack;
        
    });

    async function LoadSetupSettings() {
        const resp = await costEstimator.getAutoGenerateEstimateSetupSettings();

        if (!resp) {
            popAlert("There was an error pulling the Auto Generate Setup Settings", "Auto Generate Setup Settings");
            return;
        }

        setupData.setupSettings = resp;
        setupData.selectedProviderEntity = setupData.setupSettings.setupInfo.defaultProviderEntityId;
        setupData.selectedLocationEntity = setupData.setupSettings.setupInfo.defaultServiceLocationEntityId;
        setupData.selectedAppointmentStatusId = setupData.setupSettings.appointmentStatusList.map(item => item.appointmentStatusId);
        setupData.selectedAppointmentTypeId = setupData.setupSettings.appointmentTypeList.map(item => item.appointmentTypeId);
        setupData.selectedResourceId = setupData.setupSettings.resourceEntityList.map(item => item.resourceEntityId);
    }

    async function LoadLogResults() {
        const resp = await costEstimator.getAutoGenerateLogResults(logData.daysBack);

        if (!resp) {
            popAlert("There was an error getting the Auto Generate Logs", "Auto Generate Log");
            return;
        }

        logData.autoGenLogResult = resp;

        const daysParam = await costEstimator.getCompanySetting(CompanySettingParameterNames.PatientCostEstimator, 7);

        if (!daysParam) {
            logData.daysAheadParam = "7";
        }

        logData.daysAheadParam = daysParam.parameterValue;
    }

    async function saveProvider() {
        setupData.defaultProviderData = {
            providerEntityId: setupData.selectedProviderEntity
        }

        const resp = await saveEstimation.saveAutoGenerateSetupProvider(setupData.defaultProviderData);

        if (!resp) {
            popAlert("There was an error saving the default provider", "Auto Generate Setup Settings");
        }

        if (!resp.isSuccess) {
            popAlert(resp.message, "Auto Generate Setup Settings");
        }         
    }

    async function saveLocation() {
        setupData.defaultLocationData = {
            locationEntityId: setupData.selectedLocationEntity
        }

        const resp = await saveEstimation.saveAutoGenerateSetupLocation(setupData.defaultLocationData);

        if (!resp) {
            popAlert("There was an error saving the default service location", "Auto Generate Setup Settings");
        }

        if (!resp.isSuccess) {
            popAlert(resp.message, "Auto Generate Setup Settings");
        }         
    }

    async function saveApptStatus() {
        setupData.defaultApptStatusData = {
            apptStatusId: setupData.selectedAppointmentStatusId
        }
        const resp = await saveEstimation.saveAutoGenerateSetupApptStatus(setupData.defaultApptStatusData);

        if (!resp) {
            popAlert("There was an error saving the default appt statuses", "Auto Generate Setup Settings");
        }

        if (!resp.isSuccess) {
            popAlert(resp.message, "Auto Generate Setup Settings");
        }         
    }

    async function saveApptType() {
        setupData.defaultApptTypeData = {
            apptTypeId: setupData.selectedAppointmentTypeId
        }
        const resp = await saveEstimation.saveAutoGenerateSetupApptType(setupData.defaultApptTypeData);

        if (!resp) {
            popAlert("There was an error saving the default appt types", "Auto Generate Setup Settings");
        }

        if (!resp.isSuccess) {
            popAlert(resp.message, "Auto Generate Setup Settings");
        }     
    }

    async function saveResource() {
        setupData.defaultResourceData = {
            resourceEntityId: setupData.selectedResourceId
        }
        const resp = await saveEstimation.saveAutoGenerateSetupResource(setupData.defaultResourceData);

        if (!resp) {
            popAlert("There was an error saving the default resources", "Auto Generate Setup Settings");
        }

        if (!resp.isSuccess) {
            popAlert(resp.message, "Auto Generate Setup Settings");
        }  
    }

    async function saveAllSettings() {
        await saveProvider();
        await saveLocation();
        await saveApptStatus();
        await saveApptType();
        await saveResource();
    }

    async function searchLogs() {
        await LoadLogResults();
    }

</script>

<style scoped>
    .nav-tabs {
        border-bottom: 1px solid #dee2e6;
    }
    .nav-link {
        border: 1px solid transparent;
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
        padding: 0.5rem 1rem;
        cursor: pointer;
    }
    .nav-link.active {
        color: #495057;
        background-color: #fff;
        border-color: #dee2e6 #dee2e6 #fff;
    }
    .tab-content {
        border: 1px solid #dee2e6;
        border-top: none;
        padding: 1rem;
    }
</style>

<template>
    <PageTitle :security-screen-name="props.screenName" >
        <template #permitted>   
            <WindowHeaderFooter :headerHeight="3.5" :footerHeight="5.0">
                <template v-slot:WindowHeader>
                    <div class="isalus-page-title-large pt-3">Estimate Auto Generate Setup</div>
                </template>
                <template v-slot:WindowBody>  
                    <div class="grid-container-process label-isalus-medium">
                        <div class="pb-3">
                            In order for us to auto-generate estimates for Self Pay patients, we'll need you to establish some defaults
                            in case the self pay appointment does not have all the necessary information we need to calculate the estimate.
                        </div>

                        <div>
                            <!-- Tab Headers -->
                            <ul class="nav nav-tabs">
                                <li class="nav-item" v-for="tab in tabs" :key="tab.name">
                                    <a class="nav-link" 
                                        :class="{ active: tab.name === activeTab }" 
                                        href="#" 
                                        @click.prevent="activeTab = tab.name">
                                        {{ tab.name }}
                                    </a>
                                </li>
                            </ul>

                            <!-- Tab Content -->
                            <div class="tab-content">
                            <div v-show="activeTab === 'Provider'">
                                <h6>Provider Setup</h6>
                                <p>Please select a default provider to be used in case we cannot tie a self-pay appointment to a specific provider.</p>
                                <div>
                                    <v-select 
                                        style="font-size: 0.75rem; width: 400px;"
                                        v-model="setupData.selectedProviderEntity"
                                        :options="setupData.providerList" 
                                        :reduce="item => +item.encode" label="decode" 
                                        :multiple="false" 
                                        :searchable="true"
                                        :filterable="true"
                                        :closeOnSelect="true"
                                        :select-on-tab="true" />
                                </div>
                                <div class="mt-3">
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.Save"
                                        :disabled="showProviderSaveButton"
                                        :text="'Save Provider'"
                                        :size="ButtonDisplaySize.Small" 
                                        :spinText="'Saving...'"
                                        :performAction="saveProvider"
                                    />
                                </div>
                            </div>
                            <div v-show="activeTab === 'Service Location'">
                                <h6>Service Location Setup</h6>
                                <p>Please select a default service location to be used in case we cannot tie a self-pay appointment to a specific location.</p>
                                <div>
                                    <v-select 
                                        style="font-size: 0.75rem; width: 400px;"
                                        v-model="setupData.selectedLocationEntity"
                                        :options="setupData.locationList" 
                                        :reduce="item => +item.encode" label="decode" 
                                        :multiple="false" 
                                        :searchable="true"
                                        :filterable="true"
                                        :closeOnSelect="true"
                                        :select-on-tab="true" />
                                </div>
                                <div class="mt-3">
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.Save"
                                        :disabled="showLocationSaveButton"
                                        :text="'Save Location'"
                                        :size="ButtonDisplaySize.Small" 
                                        :spinText="'Saving...'"
                                        :performAction="saveLocation"
                                    />
                                </div>
                            </div>
                            <div v-show="activeTab === 'Appointment Status'">
                                <h6>Appointment Status Setup</h6>
                                <p>Please select the appointment statuses that you want self-pay estimates to be auto-generated for.</p>
                                <div>
                                    <v-select 
                                        style="font-size: 0.75rem; width: 600px;"
                                        v-model="setupData.selectedAppointmentStatusId"
                                        :options="setupData.appointmentStatusList" 
                                        :reduce="item => +item.encode" label="decode" 
                                        :multiple="true" 
                                        :searchable="true"
                                        :filterable="true"
                                        :closeOnSelect="false"
                                        :select-on-tab="true" />
                                </div>                                
                                <div class="mt-3">
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.Save"
                                        :disabled="!showApptStatusSaveButton"
                                        :text="'Save Appt Status'"
                                        :size="ButtonDisplaySize.Small" 
                                        :spinText="'Saving...'"
                                        :performAction="saveApptStatus"
                                    />
                                </div>
                            </div>
                            <div v-show="activeTab === 'Appointment Type'">
                                <h6>Appointment Type Setup</h6>
                                <p>Please select the appointment types that you want self-pay estimates to be auto-generated for.</p>
                                <div>
                                    <v-select 
                                        style="font-size: 0.75rem; width: 600px;"
                                        v-model="setupData.selectedAppointmentTypeId"
                                        :options="setupData.appointmentTypeList" 
                                        :reduce="item => +item.encode" label="decode" 
                                        :multiple="true" 
                                        :searchable="true"
                                        :filterable="true"
                                        :closeOnSelect="false"
                                        :select-on-tab="true" />
                                </div>                             
                                <div class="mt-3">
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.Save"
                                        :disabled="!showApptTypeSaveButton"
                                        :text="'Save Appt Type'"
                                        :size="ButtonDisplaySize.Small" 
                                        :spinText="'Saving...'"
                                        :performAction="saveApptType"
                                    />
                                </div>
                            </div>
                            <div v-show="activeTab === 'Resource'">
                                <h6>Resource Setup</h6>
                                <p>Please select the resources that you want self-pay estimates to be auto-generated for.</p>
                                <div>
                                    <v-select 
                                        style="font-size: 0.75rem; width: 600px;"
                                        v-model="setupData.selectedResourceId"
                                        :options="setupData.resourceList" 
                                        :reduce="item => +item.encode" label="decode" 
                                        :multiple="true" 
                                        :searchable="true"
                                        :filterable="true"
                                        :closeOnSelect="false"
                                        :select-on-tab="true" />
                                </div>                        
                                <div class="mt-3">
                                    <ActionButtonV2 
                                        :action="ActionButtonStyle.Save"
                                        :disabled="!showResourceSaveButton"
                                        :text="'Save Resource'"
                                        :size="ButtonDisplaySize.Small" 
                                        :spinText="'Saving...'"
                                        :performAction="saveResource"
                                    />
                                </div>
                            </div> 
                            <div v-show="activeTab === 'Log'">
                                <h6>Estimate Auto Generation Log</h6>
                                <p>By default, we show the last 3 days of activity.  You can filter on any field to limit your results,
                                   or search up to {{ logData.maxDaysBack }} days back. Based on your current company setting, we are 
                                   auto-generating self pay estimates {{ logData.daysAheadParam }} days ahead of appointments.
                                </p>
                                <div class="row pb-1">
                                    <div class="col col-md-1">
                                        Days Back: 
                                    </div>
                                    <div class="col col-md-1">
                                        <IntegerInput 
                                            v-model:numberValue="logData.daysBack" 
                                            :maxLength=3 
                                            :startingRange=1
                                            :endingRange=logData.maxDaysBack
                                            :positive=true
                                            :required=true
                                            :alignment=Alignment.right
                                            :error-display="true"
                                        /> 
                                    </div>
                                    <div class="col col-md-2">
                                        <ActionButtonV2 
                                            :disabled="errorDaysBack"
                                            :action="ActionButtonStyle.Search"
                                            :text="'Search Logs'"
                                            :size="ButtonDisplaySize.Small" 
                                            :spinText="'Searching...'"
                                            :performAction="searchLogs"
                                        />
                                    </div>
                                    <div v-if="errorDaysBack" class="col col-md-8 text-danger">Valid days back are 1-{{ logData.maxDaysBack }}.</div>
                                </div>
                                <div v-if="logData.autoGenLogResult.length > 0">
                                    <ChoiceTable
                                        :columns="logColumns" 
                                        :data="logData.autoGenLogResult"
                                        :height="500" 
                                        :show-filters="true" 
                                        :allowSorting="true"                                        
                                        fontSize="smaller"
                                        :key-value-data-member="'logId'">
                                    </ChoiceTable>
                                </div>   
                                <div v-if="logData.autoGenLogResult.length < 1">
                                    No logs to display
                                </div>
                            </div>                            
                        </div>
                    </div>
                    </div>  
                </template>
                <template v-slot:WindowFooter> 
                    <div class="text-end">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Save"
                            :text="'Save All Settings'"
                            :disabled="!showSaveAllButton"
                            :size="ButtonDisplaySize.Default" 
                            :spinText="'Saving'"
                            :performAction="saveAllSettings"
                            :endingPadding="1"
                        />
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Close"
                            :text="'Close'"
                            :size="ButtonDisplaySize.Default" 
                            :spinText="'Processing'"
                            :performAction="closeWindow"
                        />
                    </div>
                </template>
            </WindowHeaderFooter> 
        </template>
    </PageTitle>
</template>
